import { useLocation } from "react-router-dom";
import { ROUTES } from "../../routes/paths";

/**
 * The path the navigation should light for, out of the router state a
 * navigation carried — or nothing, for the ordinary case where the URL speaks
 * for itself.
 *
 * A screen can be reached from a section that is not the one its URL sits
 * under: a plan opened from a client's Plans tab lives at `/create-plan/:id`,
 * and lighting Create plan for it says the user left the client they are
 * plainly still inside. `origin` is the same state the plan builder reads for
 * its breadcrumb and its way back out (see `PlanDetailPage`), so the rail, the
 * phone's tab bar and the trail all agree on where the screen was entered from.
 *
 * Read defensively: state is whatever the last navigation put there, and a
 * screen opened by its URL alone has none. It survives a reload — the browser
 * keeps history state — so a refreshed plan keeps the section it came from.
 */
const originPath = (state: unknown): string | undefined => {
  const to = (state as { origin?: { to?: unknown } } | null)?.origin?.to;
  return typeof to === "string" ? to : undefined;
};

/**
 * What the two navigations mark as current. Shared so the rail on a desktop
 * and the tab bar on a phone cannot come to different answers about which
 * section the reader is standing in.
 */
export const useActivePath = (): string => {
  const location = useLocation();
  return originPath(location.state) ?? location.pathname;
};


/**
 * The four screens a phone is never inside something else on: the three tabs
 * of the bar and the My task circle beside it — see `MobileTabBar`, which
 * draws them in that order and names them.
 *
 * They are where a phone starts, so there is nothing above them to go back up
 * to: `PageHeader` draws no back arrow on one. Everywhere else was opened from
 * somewhere, the account menu included, and carries the arrow.
 *
 * Revenue was the fourth until My task took the circle. It is reached from the
 * account menu now, which makes it a screen opened from somewhere rather than
 * one a phone starts on, so it gains the arrow back the way every other row of
 * that menu has one.
 */
export const SECTION_ROOTS: readonly string[] = [
  ROUTES.dashboard,
  ROUTES.myClients,
  ROUTES.enquiries,
  ROUTES.myTask,
];

/** Whether the path is one of those four, rather than a screen under one. */
export const isSectionRoot = (pathname: string): boolean =>
  SECTION_ROOTS.includes(pathname);

export default useActivePath;
